import Button, { ButtonType } from '@/components/Button';
import Text from '@/components/Text';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface FeatureLinkCardProps {
  href?: string;
  onClick?: () => void;
  title: string;
  description: string;
  cta: string;
  icon?: IconProp;
  iconSrc?: string | StaticImageData;
  iconNode?: React.ReactNode;
  className?: string;
  fillHeight?: boolean;
  animateOnMount?: boolean;
  horizontalDesktop?: boolean;
}

export default function FeatureLinkCard({
  href,
  onClick,
  title,
  description,
  cta,
  icon,
  iconSrc,
  iconNode,
  className,
  fillHeight = false,
  animateOnMount = false,
  horizontalDesktop = false,
}: FeatureLinkCardProps) {
  const [isVisible, setIsVisible] = useState(!animateOnMount);

  useEffect(() => {
    if (!animateOnMount) return;
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [animateOnMount]);

  const wrapperClassName = twMerge(
    'block w-full',
    fillHeight && 'h-full',
    onClick && 'cursor-pointer',
    animateOnMount && 'transition-all duration-700 ease-out',
    animateOnMount && (isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4')
  );

  const article = (
    <article
      className={twMerge(
        'group flex min-h-[420px] w-full flex-col items-center justify-center gap-4 rounded-2xl bg-[linear-gradient(134.97deg,#0A1A63_0%,#1658FF_99.95%)] px-6 pt-8 pb-10 text-center transition-all duration-300 hover:brightness-110 md:min-h-[368px]',
        horizontalDesktop &&
          'md:min-h-0 md:flex-row md:items-center md:justify-start md:gap-8 md:px-8 md:py-5 md:text-left',
        fillHeight && 'h-full',
        className
      )}
    >
      <div
        className={twMerge(
          'flex h-32 w-32 items-center justify-center md:h-40 md:w-40 xl:h-48 xl:w-48',
          horizontalDesktop && 'md:h-32 md:w-32 md:flex-shrink-0 xl:h-36 xl:w-36'
        )}
      >
        {iconNode ? (
          iconNode
        ) : iconSrc ? (
          <div className="relative h-full w-full">
            <Image src={iconSrc} alt={title} fill className="object-contain" />
          </div>
        ) : icon ? (
          <div className="opacity-80">
            <FontAwesomeIcon icon={icon} className="size-24 text-white" />
          </div>
        ) : null}
      </div>

      <div
        className={twMerge(
          'flex w-full max-w-[480px] flex-col items-center gap-4',
          horizontalDesktop && 'md:flex-1 md:max-w-none md:items-start md:gap-3'
        )}
      >
        <div className={twMerge('flex w-full flex-col items-center', horizontalDesktop && 'md:items-start')}>
          <h3 className="w-full font-game font-normal text-title-1 uppercase text-white">
            {title}
          </h3>
          <Text className={twMerge('w-full font-normal text-title-3 text-white', horizontalDesktop && 'md:max-w-[720px]')}>
            {description}
          </Text>
        </div>

        {!horizontalDesktop && (
          <Button
            type={ButtonType.Primary}
            size="md"
            className="pointer-events-none h-14 rounded-full bg-[#1658FF] px-8 text-title-3 text-white"
          >
            {cta}
          </Button>
        )}
      </div>

      {horizontalDesktop && (
        <Button
          type={ButtonType.Primary}
          size="md"
          className="pointer-events-none h-14 flex-shrink-0 rounded-full bg-[#1658FF] px-8 text-title-3 text-white"
        >
          {cta}
        </Button>
      )}
    </article>
  );

  if (onClick) {
    return (
      <div onClick={onClick} className={wrapperClassName}>
        {article}
      </div>
    );
  }

  if (href) {
    return (
      <Link href={href} className={wrapperClassName}>
        {article}
      </Link>
    );
  }

  return <div className={wrapperClassName}>{article}</div>;
}
